import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ShoppingBag } from "lucide-react";
import { useCart } from "@/contexts/CartContext";
import SEO from "@/components/SEO";
import ShopPayButton from "@/components/ShopPayButton";
import { shopifyImg, shopifySrcSet, SIZES_PDP_MAIN } from "@/lib/format";

// Product page for Shopify's digital gift card: pick an amount, add to cart or Shop Pay.
// Shopify emails the gift card code after checkout.
export default function GiftCardProduct({ product }) {
  const { addItem, setIsOpen } = useCart();
  const variants = (product.variants || []).filter((v) => v.available !== false);
  const [variant, setVariant] = useState(variants[0] || null);
  const image = product.images?.[0];

  const add = () => {
    if (!variant) return;
    addItem({ ...product, price: variant.price }, variant.title, "", 1, variant.id);
    setIsOpen(true);
  };

  return (
    <div data-testid="gift-card-page" className="bg-[#0A0A0A]">
      <SEO title={`${product.name} | AERWEAR`} description="AERWEAR digital gift card, sent by email after checkout." path={`/products/${product.id}`} />
      <div className="px-4 md:px-12 py-3 md:py-4">
        <Link to="/shop/men" className="inline-flex items-center gap-1 text-[10px] text-[#8A8A8A] hover:text-[#F2F0EB] uppercase tracking-[0.2em] transition-colors">
          <ChevronLeft size={12} /> Shop
        </Link>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2">
        <div className="aspect-[4/5] md:aspect-auto md:h-[70vh] bg-[#141414] border-b md:border-b-0 md:border-r border-[#8A8A8A]/20 overflow-hidden">
          {image ? (
            <img src={shopifyImg(image, 1080)} srcSet={shopifySrcSet(image, [540, 720, 1080])} sizes={SIZES_PDP_MAIN} width="1080" height="1350" alt="AERWEAR gift card" className="w-full h-full object-cover" />
          ) : (
            <div className="flex items-center justify-center h-full text-[#8A8A8A]/30"><ShoppingBag size={64} strokeWidth={1} /></div>
          )}
        </div>
        <div className="px-6 md:px-12 lg:px-16 py-10 md:py-16">
          <h1 className="text-2xl md:text-3xl font-medium uppercase tracking-[0.15em] text-[#F2F0EB]">{product.name}</h1>
          <p className="mt-3 text-sm text-[#D9D7D2]">Digital gift card. Sent by email after checkout.</p>
          <div className="w-full h-px bg-[#8A8A8A]/20 my-8" />
          {variants.length > 0 ? (
            <>
              <span className="text-[10px] uppercase tracking-[0.2em] text-[#8A8A8A] mb-3 block">Amount</span>
              <div className="flex gap-2 flex-wrap">
                {variants.map((v) => (
                  <button key={v.id} type="button" data-testid={`gift-amount-${v.price}`} onClick={() => setVariant(v)}
                    className={`min-w-[64px] py-3 px-4 text-xs transition-colors ${variant?.id === v.id ? "bg-[#F2F0EB] text-[#0A0A0A] border border-[#F2F0EB] font-medium" : "text-[#8A8A8A] border border-[#8A8A8A]/30 hover:border-[#F2F0EB] hover:text-[#F2F0EB]"}`}>
                    ${Number(v.price).toFixed(0)}
                  </button>
                ))}
              </div>
              <button data-testid="gift-add-to-cart" type="button" onClick={add} className="mt-8 w-full bg-[#F2F0EB] text-[#0A0A0A] py-4 font-['Oswald'] text-xs uppercase tracking-[0.25em] font-semibold hover:bg-[#8A8A8A] transition-colors">
                Add To Cart
              </button>
              <ShopPayButton variantId={variant?.id} quantity={1} />
            </>
          ) : (
            <p className="text-sm text-[#8A8A8A]">Gift cards are unavailable right now.</p>
          )}
        </div>
      </div>
    </div>
  );
}
